import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonAdd } from "@/components/ui/button";
import ProductFormDrawer from "@/components/menu/ProductFormDrawer";
import ProductCard, {
  ProductCardSkeleton,
} from "@/components/menu/ProductCard";
import {
  useGetProductsQuery,
  useDeleteProductMutation,
  useGetMenusQuery,
} from "@/redux/services/menu/menu.api";
import {
  buildProductFromPrices,
  needsProductFromPrice,
} from "@/lib/productPrice";
import { Product } from "@/redux/services/menu/menu.type";
import { useAppSelector } from "@/redux/features/hooks";
import { usePermissions } from "@/hooks/usePermissions";
import { Roles } from "@/redux/services/user/user.type";
import { useModal } from "@/lib/hooks";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { Input } from "@/components/Input";
import { Utensils, Search, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";

const PAGE_SIZE = 20;
// Restaurants have a handful of menus; one page covers them all.
const MENUS_LIMIT = 100;

const TabsProduct = () => {
  const { currentRestaurant, currentUser } = useAppSelector((s) => s.user);
  const { canManage } = usePermissions();
  const confirm = useConfirm();

  const isReadOnly =
    currentUser?.roles?.includes(Roles.owner) || !canManage("menu");

  // A dish picked in the global search lands here as ?q=<title>&dish=<id>.
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParam = searchParams.get("q");
  const dishParam = searchParams.get("dish");

  const [search, setSearch] = useState(searchParam ?? "");
  const [page, setPage] = useState(1);
  // Pages are accumulated here rather than replaced, so "load more" keeps what
  // is already on screen.
  const [items, setItems] = useState<Product[]>([]);
  // A page that comes back short is the last one — a hard stop that does not
  // depend on the total count matching, so auto-load can never loop.
  const [reachedEnd, setReachedEnd] = useState(false);

  const { data, currentData, isFetching, isLoading } = useGetProductsQuery(
    {
      restaurantId: currentRestaurant?.id as string,
      q: search.trim() || undefined,
      page,
      limit: PAGE_SIZE,
    },
    { skip: !currentRestaurant?.id }
  );

  // Page 1 replaces (new search, reset, or refetch after a change); later pages
  // append, de-duped so a refetch can't double up a row.
  useEffect(() => {
    if (!data?.results) return;
    setItems((prev) =>
      page === 1
        ? data.results
        : [
            ...prev,
            ...data.results.filter((r) => !prev.some((p) => p.id === r.id)),
          ]
    );
    setReachedEnd(data.results.length < PAGE_SIZE);
  }, [data, page]);

  // A dish priced only through its size option has basePrice 0; show its
  // smallest size instead, read from the menus' placements (SFRESTO-146).
  // Loaded only when such a dish is on screen.
  const { data: menusData } = useGetMenusQuery(
    { restaurantId: currentRestaurant?.id, page: 1, limit: MENUS_LIMIT },
    { skip: !currentRestaurant?.id || !needsProductFromPrice(items) }
  );
  const fromPrices = useMemo(
    () => buildProductFromPrices(menusData?.results),
    [menusData]
  );

  const total = data?.count ?? 0;
  const hasMore = !reachedEnd && items.length < total;

  const { isOpen, item, closeModal, openEditModal, openModal } =
    useModal<Product>();
  const [deleteProduct] = useDeleteProductMutation();

  const onSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
    setReachedEnd(false);
  };

  useEffect(() => {
    if (searchParam !== null) onSearchChange(searchParam);
  }, [searchParam]);

  // Open the searched dish once it is listed, then drop the param so closing
  // the drawer does not reopen it. Read-only users just see it filtered.
  useEffect(() => {
    if (!dishParam || !currentData) return;
    // Wait for the list filtered on the searched title.
    if (searchParam !== null && search !== searchParam) return;
    const dish = currentData.results.find((p) => p.id === dishParam);
    if (dish && !isReadOnly) openEditModal(dish);
    const next = new URLSearchParams(searchParams);
    next.delete("dish");
    setSearchParams(next, { replace: true });
  }, [dishParam, currentData, search]);

  // After a change, the products cache is invalidated; rebuild the list from
  // page one so accumulated pages can't go stale or duplicate.
  const resetToFirstPage = useCallback(() => {
    setPage(1);
    setReachedEnd(false);
  }, []);

  const handleDelete = async (product: Product) => {
    const onMenus = product.menuNames?.length ?? 0;
    const ok = await confirm({
      title: `Supprimer "${product.title}" ?`,
      description:
        onMenus > 0
          ? `Ce plat est présent sur ${onMenus} menu${
              onMenus > 1 ? "s" : ""
            }. Le supprimer le retirera de tous les menus.`
          : "Cette action est irréversible.",
      confirmText: "Supprimer",
    });
    if (!ok) return;

    const res = await deleteProduct(product.id);
    if ("error" in res) {
      toast.error("Une erreur est survenue lors de la suppression");
    } else {
      toast.success("Plat supprimé");
      resetToFirstPage();
    }
  };

  // Auto-load the next page when the sentinel near the bottom scrolls into
  // view. The guard is read through a ref so the observer stays stable across
  // fetches — re-creating it on every isFetching change would re-fire on an
  // already-visible sentinel and load pages in a burst.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const canLoadRef = useRef(false);
  canLoadRef.current = hasMore && !isFetching;
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && canLoadRef.current) {
          setPage((p) => p + 1);
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore]);

  const showInitialSkeleton = isLoading && items.length === 0;

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Rechercher un plat..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9 h-10"
          />
        </div>
        {!isReadOnly && (
          <ButtonAdd label="Créer un plat" onClick={openModal} />
        )}
      </div>

      {showInitialSkeleton ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {Array.from({ length: 10 }).map((_, i) => (
            <ProductCardSkeleton key={i} />
          ))}
        </div>
      ) : items.length > 0 ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {items.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                fromPrice={fromPrices.get(product.id)}
                onEdit={openEditModal}
                onDelete={handleDelete}
                isReadOnly={isReadOnly}
              />
            ))}
          </div>

          {hasMore && (
            <div
              ref={sentinelRef}
              className="mt-8 flex flex-col items-center gap-2"
            >
              {isFetching ? (
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Chargement...
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setPage((p) => p + 1)}
                  className="text-sm font-medium text-primary transition-colors hover:underline"
                >
                  Voir plus
                </button>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-12">
          <Utensils className="h-12 w-12 text-muted-foreground opacity-30 mb-3" />
          <p className="text-muted-foreground text-lg mb-2">
            {search.trim() ? "Aucun plat trouvé" : "Aucun plat"}
          </p>
          <p className="text-muted-foreground text-sm">
            {search.trim()
              ? "Essayez un autre terme de recherche"
              : 'Cliquez sur "Créer un plat" pour commencer'}
          </p>
        </div>
      )}

      <ProductFormDrawer
        product={item}
        isOpen={isOpen}
        onClose={closeModal}
        onSaved={resetToFirstPage}
      />
    </>
  );
};

export default TabsProduct;
